/// RMS envelope detector for Morse tone reception.
///
/// Stateless math; pure-function-callable so the UI's `tick()` loop
/// can pass in a fresh audio frame each animation frame and get back
/// (new state, transition events) without touching the browser API.
///
/// Algorithm: compute the RMS amplitude of the input samples, compare
/// against a threshold with hysteresis (separate on/off levels) to
/// avoid flicker around the boundary. A single "start" or "end" event
/// is emitted on each state transition; no event when the level stays
/// in the same band.
///
/// Hysteresis is essential for noisy inputs: an oscillator playing a
/// CW tone is loud and clean, but a phone listening to that tone over
/// the air picks up room noise and microphone self-noise. Without
/// hysteresis a quiet patch in the middle of a "dah" would emit a
/// false end+start pair and corrupt the decoded character.

export interface DetectorConfig {
  /// RMS level (0..1) at which an off→on transition fires.
  threshold: number;
  /// How much lower than `threshold` the level must drop to fire an
  /// on→off transition. Typically 0.1–0.2× threshold.
  hysteresis: number;
}

export interface DetectorState {
  isOn: boolean;
  /// Latest computed RMS — exposed so the UI can render a level
  /// meter without re-computing.
  lastRms: number;
}

export interface DetectorEvent {
  type: "start" | "end";
  /// Caller-assigned timestamp (any monotonic ms). The detector
  /// doesn't know what time it is — it just labels the events with
  /// what the caller passed in.
  timeMs: number;
}

export const INITIAL_DETECTOR_STATE: DetectorState = { isOn: false, lastRms: 0 };

/// RMS amplitude of a time-domain sample buffer. Equivalent to
/// `sqrt(mean(samples²))`. Returns 0 for an empty buffer.
export function computeRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sumSq = 0;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    sumSq += s * s;
  }
  return Math.sqrt(sumSq / samples.length);
}

export interface DetectStep {
  state: DetectorState;
  events: DetectorEvent[];
}

/// Compute the new state and any transition events for one audio
/// frame. Pure — no globals, no Web Audio dependency.
export function detect(
  samples: Float32Array,
  timeMs: number,
  state: DetectorState,
  config: DetectorConfig,
): DetectStep {
  const rms = computeRms(samples);
  const offLevel = Math.max(0, config.threshold - config.hysteresis);
  const events: DetectorEvent[] = [];
  let isOn = state.isOn;
  if (isOn && rms < offLevel) {
    events.push({ type: "end", timeMs });
    isOn = false;
  } else if (!isOn && rms >= config.threshold) {
    events.push({ type: "start", timeMs });
    isOn = true;
  }
  return { state: { isOn, lastRms: rms }, events };
}
